import React from 'react'
import { Input, Modal, Typography } from 'antd'
import { AxiosError } from 'axios'
import { authApi } from 'api/auth'
import {
  getContactCodeCooldownSeconds,
  setContactCodeCooldown
} from 'shared/utils/contactCodeCooldown'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import styles from './EmailVerificationModal.module.scss'

const CODE_LENGTH = 4

interface IEmailVerificationModalProps {
  isOpen: boolean
  email: string
  onClose: () => void
  onVerified: (email: string) => void
}

export const EmailVerificationModal: React.FC<IEmailVerificationModalProps> = ({
  isOpen,
  email,
  onClose,
  onVerified
}) => {
  const [isVerifying, setIsVerifying] = React.useState(false)
  const [error, setError] = React.useState('')
  const [timer, setTimer] = React.useState(0)
  // ремонтируем поля кода, чтобы очистить их после неверной попытки
  const [codeFieldKey, setCodeFieldKey] = React.useState(0)

  React.useEffect(() => {
    if (!isOpen) return

    setError('')
    setCodeFieldKey((key) => key + 1)
    setTimer(getContactCodeCooldownSeconds(email))

    const interval = setInterval(
      () => setTimer(getContactCodeCooldownSeconds(email)),
      1000
    )
    return () => clearInterval(interval)
  }, [isOpen, email])

  const verifyCode = async (code: string) => {
    try {
      setIsVerifying(true)
      setError('')
      const { data } = await authApi.verifyEmailAttachCode({
        code: Number(code)
      })
      showMessage('success', data.message, '')
      onVerified(email)
      onClose()
    } catch (e) {
      const message = (e as AxiosError<{ message: string }>).response?.data
        ?.message
      setError(message ?? 'Неверный код подтверждения')
      setCodeFieldKey((key) => key + 1)
    } finally {
      setIsVerifying(false)
    }
  }

  const resendCode = async () => {
    try {
      await authApi.requestEmailAttachCode({ email })
      setContactCodeCooldown(email)
      setTimer(getContactCodeCooldownSeconds(email))
      setError('')
      setCodeFieldKey((key) => key + 1)
      showMessage('success', 'Код отправлен повторно', '')
    } catch (e) {
      const message = (e as AxiosError<{ message: string }>).response?.data
        ?.message
      // код уже отправлен, а вкладка об этом не знает — заводим таймер по ответу
      if (message?.includes('секунд')) {
        setContactCodeCooldown(email)
        setTimer(getContactCodeCooldownSeconds(email))
      }
      showServerError(e)
    }
  }

  return (
    <Modal
      open={isOpen}
      title='Подтверждение почты'
      footer={null}
      onCancel={onClose}
    >
      <div className={styles.wrapper}>
        <Typography.Text>
          Код подтверждения отправлен на {email}. Код действует 10 минут.
        </Typography.Text>
        <Input.OTP
          key={codeFieldKey}
          length={CODE_LENGTH}
          disabled={isVerifying}
          status={error ? 'error' : undefined}
          formatter={(value) => value.replace(/\D/g, '')}
          onChange={verifyCode}
        />
        {error && <Typography.Text type='danger'>{error}</Typography.Text>}
        <Typography.Link
          disabled={timer > 0 || isVerifying}
          onClick={resendCode}
        >
          {timer > 0
            ? `Отправить код повторно можно через ${timer} сек`
            : 'Отправить код повторно'}
        </Typography.Link>
      </div>
    </Modal>
  )
}
